import React, { useState, useEffect, useCallback, useRef } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { format } from "date-fns";
import toast from "react-hot-toast";
import * as FileSaver from "file-saver";
import XLSX from "sheetjs-style";

import {
  retrieveAllBlendsApi,
  retrieveBlendVersionsApi,
} from "../../../components/api/BlendApiService";
import {
  retrieveBatchesByBlendVersionApi,
  retrieveBatchCostComponentsApi,
} from "../../../components/api/BatchApiService";

// ─── Constants ───────────────────────────────────────────────────────────────

// Default admin cost per kg — used as fallback for records created before this field was introduced
const DEFAULT_ADMIN_COST_EUR_PER_KG = 0.95;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (v, d = 2) =>
  v != null && !isNaN(Number(v))
    ? Number(v).toLocaleString("en", {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      })
    : "—";

const fmtDate = (v) => {
  if (!v) return "—";
  try { return format(new Date(v), "dd.MM.yyyy"); } catch { return String(v); }
};

/** Derive cost breakdown from component list + batch */
function computeCosts(batch, components) {
  const dietOutNet = Number(batch.dietOutNet) || 0;
  const rawIn      = Number(batch.rawIn)      || 0;
  const wastage    = rawIn > 0 ? ((dietOutNet - rawIn) / dietOutNet) * 100 : null;
  const adminCostRate = Number(batch.adminCostEurPerKg) > 0
    ? Number(batch.adminCostEurPerKg)
    : DEFAULT_ADMIN_COST_EUR_PER_KG;

  let tobaccoNet = 0;
  let customsTax = 0;
  let packaging  = 0;

  if (components && components.length > 0) {
    components.forEach((c) => {
      if (c.componentType === "TOBACCO") {
        tobaccoNet += Number(c.totalCostEur)   || 0;
        customsTax += Number(c.customsDutyEur) || 0;
      } else {
        packaging  += Number(c.totalCostEur)   || 0;
      }
    });
    tobaccoNet = tobaccoNet - customsTax; // totalCostEur for TOBACCO already includes customs
  } else {
    const storedTobacco   = Number(batch.tobaccoPgEur)     || 0;
    const storedProcFee   = Number(batch.processingFeeEur) || 0;
    const storedUnitPrice = Number(batch.unitPriceEur)     || 0;
    tobaccoNet = storedTobacco;
    packaging  =
      dietOutNet > 0 && storedUnitPrice > 0
        ? Math.max(0, storedUnitPrice * dietOutNet - storedTobacco - storedProcFee)
        : 0;
  }

  const processing = Number(batch.processingFeeEur) || 0;
  const adminTotal = adminCostRate * dietOutNet;

  const tobaccoAndPg = tobaccoNet + customsTax;
  const totalCostEur = tobaccoAndPg + packaging + processing; // without admin
  const grandTotal   = totalCostEur + adminTotal;

  const storedUnitPrice = Number(batch.unitPriceEur) || null;
  const unitPriceWithAdmin = dietOutNet > 0 ? grandTotal / dietOutNet : null;

  const perKg = (v) => (dietOutNet > 0 ? v / dietOutNet : null);

  return {
    dietOutNet,
    rawIn,
    wastage,
    tobaccoNet,
    customsTax,
    tobaccoAndPg,
    packaging,
    processing,
    adminTotal,
    adminCostRate,
    totalCostEur,
    grandTotal,
    storedUnitPrice,
    unitPriceWithAdmin,
    tobaccoPerKg:    perKg(tobaccoNet),
    customsPerKg:    perKg(customsTax),
    packagingPerKg:  perKg(packaging),
    processingPerKg: perKg(processing),
    hasComponents:   !!(components && components.length > 0),
  };
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function BatchCostOverview() {
  const [blends, setBlends]               = useState([]);
  const [versions, setVersions]           = useState([]);
  const [selectedBlendIds, setSelectedBlendIds] = useState([]);
  const [selectedVersionIds, setSelectedVersionIds] = useState([]);
  const [loadingBlends, setLoadingBlends] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);

  const [rows, setRows]       = useState([]); // { batch, costs, loading, error }
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0); // 0-100

  const cancelRef = useRef(false);

  // ── Load blends ──
  useEffect(() => {
    setLoadingBlends(true);
    retrieveAllBlendsApi()
      .then((res) => setBlends(res?.data || []))
      .catch(() => toast.error("Could not load blends."))
      .finally(() => setLoadingBlends(false));
  }, []);

  // ── Load versions when blend selection changes ──
  useEffect(() => {
    setVersions([]);
    setSelectedVersionIds([]);
    setRows([]);
    if (selectedBlendIds.length === 0) return;
    setLoadingVersions(true);
    Promise.all(
      selectedBlendIds.map((blendId) =>
        retrieveBlendVersionsApi(blendId)
          .then((res) => (res?.data || []).map((v) => ({ ...v, blendId })))
          .catch(() => [])
      )
    )
      .then((results) => setVersions(results.flat()))
      .catch(() => toast.error("Could not load versions."))
      .finally(() => setLoadingVersions(false));
  }, [selectedBlendIds]);

  // ── Load batches + cost components ──
  const loadAll = useCallback(async () => {
    if (selectedBlendIds.length === 0) { setRows([]); return; }

    cancelRef.current = false;
    setLoadingBatches(true);
    setLoadingProgress(0);
    setRows([]);

    try {
      const versionsToFetch =
        selectedVersionIds.length > 0
          ? versions.filter((v) => selectedVersionIds.includes(String(v.id)))
          : versions;

      // Step 1: fetch all batches
      const batchResults = await Promise.all(
        versionsToFetch.map((ver) => {
          const blendId = ver.blendId ?? selectedBlendIds[0];
          return retrieveBatchesByBlendVersionApi(blendId, ver.id)
            .then((res) =>
              (res?.data || []).map((b) => ({
                ...b,
                blendId: b.blendId ?? blendId,
                blendVersionId: b.blendVersionId ?? ver.id,
                blendName:
                  b.blendName ||
                  blends.find((bl) => String(bl.id) === String(blendId))?.name ||
                  String(blendId),
                blendVersionName: b.blendVersionName || ver.versionName || String(ver.id),
              }))
            )
            .catch(() => []);
        })
      );

      const allBatches = batchResults.flat();
      if (cancelRef.current) return;

      // Show skeleton rows immediately
      setRows(allBatches.map((b) => ({ batch: b, costs: null, loading: true, error: false })));

      // Step 2: fetch cost components for all batches in parallel
      const total = allBatches.length;
      let done = 0;

      await Promise.all(
        allBatches.map(async (batch, idx) => {
          try {
            const res = await retrieveBatchCostComponentsApi(batch.id);
            const components = res?.data || [];
            const costs = computeCosts(batch, components);
            if (!cancelRef.current) {
              setRows((prev) => {
                const next = [...prev];
                next[idx] = { batch, costs, loading: false, error: false };
                return next;
              });
            }
          } catch {
            if (!cancelRef.current) {
              setRows((prev) => {
                const next = [...prev];
                next[idx] = { batch, costs: computeCosts(batch, null), loading: false, error: true };
                return next;
              });
            }
          } finally {
            done += 1;
            if (!cancelRef.current) setLoadingProgress(Math.round((done / total) * 100));
          }
        })
      );
    } catch {
      toast.error("Could not load batches.");
    } finally {
      if (!cancelRef.current) setLoadingBatches(false);
    }
  }, [selectedBlendIds, selectedVersionIds, blends, versions]);

  useEffect(() => {
    loadAll();
    return () => { cancelRef.current = true; };
  }, [loadAll]);

  // ── Export to Excel ──
  const handleExport = () => {
    if (rows.length === 0) return;

    const data = rows.map(({ batch, costs }) => {
      const c = costs || {};
      const processingDate = batch.processingDate ?? batch.dietProcessingDate ?? batch.primaryProcessingDate;
      return {
        Blend:            batch.blendName || "",
        "Batch ID":       batch.id,
        "Batch #":        batch.primaryId || batch.batchNumber || "",
        "DIET Batch ID":  batch.dietId || "",
        "Processing Date": processingDate ? fmtDate(processingDate) : "",
        "RAW In (kg)":    Number(c.rawIn) || "",
        "DIET Out Net (kg)": Number(c.dietOutNet) || "",
        "Tobacco (EUR)":  c.tobaccoNet?.toFixed(2) || "",
        "Custom Tax (EUR)": c.customsTax?.toFixed(2) || "",
        "Tobacco+PG (EUR)": c.tobaccoAndPg?.toFixed(2) || "",
        "Packaging (EUR)": c.packaging?.toFixed(2) || "",
        "Processing Fee (EUR)": c.processing?.toFixed(2) || "",
        "Total Cost (EUR)": c.totalCostEur?.toFixed(2) || "",
        "Unit Price (EUR/kg)": c.storedUnitPrice?.toFixed(4) || "",
        "Admin Cost (EUR)": c.adminTotal?.toFixed(2) || "",
        "Admin Cost Rate (EUR/kg)": c.adminCostRate?.toFixed(4) || "",
        "Total Cost incl. Admin (EUR)": c.grandTotal?.toFixed(2) || "",
        "Tobacco/kg":    c.tobaccoPerKg?.toFixed(4) || "",
        "Custom Tax/kg": c.customsPerKg?.toFixed(4) || "",
        "Packaging/kg":  c.packagingPerKg?.toFixed(4) || "",
        "Processing/kg": c.processingPerKg?.toFixed(4) || "",
        "Total Unit Price/kg (incl. admin)": c.unitPriceWithAdmin?.toFixed(4) || "",
        "Wastage %":     c.wastage?.toFixed(2) || "",
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Batch Cost Overview");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    FileSaver.saveAs(blob, `batch_cost_overview_${format(new Date(), "yyyyMMdd")}.xlsx`);
  };

  // ── Totals ──
  const loaded = rows.filter((r) => !r.loading && r.costs);
  const totals = loaded.reduce(
    (acc, { costs: c }) => ({
      rawIn:          acc.rawIn          + (c.rawIn          || 0),
      dietOutNet:     acc.dietOutNet     + (c.dietOutNet     || 0),
      tobaccoAndPg:   acc.tobaccoAndPg   + (c.tobaccoAndPg   || 0),
      tobaccoNet:     acc.tobaccoNet     + (c.tobaccoNet     || 0),
      customsTax:     acc.customsTax     + (c.customsTax     || 0),
      packaging:      acc.packaging      + (c.packaging      || 0),
      processing:     acc.processing     + (c.processing     || 0),
      totalCostEur:   acc.totalCostEur   + (c.totalCostEur   || 0),
      adminTotal:     acc.adminTotal     + (c.adminTotal      || 0),
      grandTotal:     acc.grandTotal     + (c.grandTotal      || 0),
    }),
    { rawIn: 0, dietOutNet: 0, tobaccoAndPg: 0, tobaccoNet: 0, customsTax: 0, packaging: 0, processing: 0, totalCostEur: 0, adminTotal: 0, grandTotal: 0 }
  );
  const totalWastage =
    totals.rawIn > 0 ? ((totals.rawIn - totals.dietOutNet) / totals.rawIn) * 100 : null;
  const totalUnitPriceWithAdmin =
    totals.dietOutNet > 0 ? totals.grandTotal / totals.dietOutNet : null;

  // ─── Shared header sx ───
  const hCell = { fontWeight: 700, fontSize: "0.72rem", whiteSpace: "nowrap", py: 0.75 };
  const dCell = { fontSize: "0.78rem", whiteSpace: "nowrap", py: 0.5 };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* ── Toolbar ── */}
      <Toolbar disableGutters sx={{ gap: 1, flexWrap: "wrap", mb: 1 }}>
        <Typography variant="h5" sx={{ mr: 2, fontWeight: 700 }}>
          Batch Cost Overview
        </Typography>

        <Autocomplete
          multiple
          size="small"
          options={blends}
          getOptionLabel={(o) => o.name || String(o.id)}
          value={blends.filter((b) => selectedBlendIds.includes(String(b.id)))}
          onChange={(_, val) => setSelectedBlendIds(val.map((b) => String(b.id)))}
          loading={loadingBlends}
          sx={{ minWidth: 260, maxWidth: 380 }}
          renderInput={(params) => (
            <TextField {...params} label="Blend(s)" placeholder="All blends" />
          )}
        />

        <Autocomplete
          multiple
          size="small"
          options={versions}
          getOptionLabel={(v) => v.versionName || String(v.id)}
          value={versions.filter((v) => selectedVersionIds.includes(String(v.id)))}
          onChange={(_, val) => setSelectedVersionIds(val.map((v) => String(v.id)))}
          loading={loadingVersions}
          disabled={versions.length === 0}
          sx={{ minWidth: 220, maxWidth: 320 }}
          renderInput={(params) => (
            <TextField {...params} label="Version(s)" placeholder="All versions" />
          )}
        />

        <Box sx={{ flex: 1 }} />

        <Tooltip title="Refresh">
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadAll}
              disabled={loadingBatches || selectedBlendIds.length === 0}
            >
              Refresh
            </Button>
          </span>
        </Tooltip>

        <Tooltip title="Export to Excel">
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              disabled={rows.length === 0}
            >
              Export
            </Button>
          </span>
        </Tooltip>
      </Toolbar>

      {/* ── Loading progress ── */}
      {loadingBatches && rows.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress variant="determinate" value={loadingProgress} sx={{ height: 6, borderRadius: 3 }} />
          <Typography variant="caption" color="text.secondary">
            Loading cost components… {loadingProgress}%
          </Typography>
        </Box>
      )}

      {selectedBlendIds.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>Select one or more blends to load batch costs.</Alert>
      )}

      {/* ── Summary cards ── */}
      {loaded.length > 0 && (
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 140 }}>
            <Typography variant="caption" color="text.secondary" display="block">Batches loaded</Typography>
            <Typography variant="h6" fontWeight={700}>{loaded.length}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 150 }}>
            <Typography variant="caption" color="text.secondary" display="block">Total DIET Out Net</Typography>
            <Typography variant="h6" fontWeight={700}>{fmt(totals.dietOutNet, 0)} kg</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 170, bgcolor: "#f9fbe7" }}>
            <Typography variant="caption" color="text.secondary" display="block">Total Cost (excl. admin)</Typography>
            <Typography variant="h6" fontWeight={700}>{fmt(totals.totalCostEur, 2)} EUR</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 170, bgcolor: "#e8f5e9" }}>
            <Typography variant="caption" color="text.secondary" display="block">Grand Total (incl. admin)</Typography>
            <Typography variant="h6" fontWeight={700}>{fmt(totals.grandTotal, 2)} EUR</Typography>
          </Paper>
          {totalUnitPriceWithAdmin != null && (
            <Paper variant="outlined" sx={{ p: 1.5, minWidth: 190, bgcolor: "#ede7f6" }}>
              <Typography variant="caption" color="text.secondary" display="block">Avg Unit Price (incl. admin)</Typography>
              <Typography variant="h6" fontWeight={700}>{fmt(totalUnitPriceWithAdmin, 4)} EUR/kg</Typography>
            </Paper>
          )}
          {totalWastage != null && (
            <Paper variant="outlined" sx={{ p: 1.5, minWidth: 140, bgcolor: totalWastage > 15 ? "#fce4ec" : "#f9fbe7" }}>
              <Typography variant="caption" color="text.secondary" display="block">Avg Wastage</Typography>
              <Typography variant="h6" fontWeight={700} color={totalWastage > 15 ? "error.main" : "text.primary"}>
                {fmt(totalWastage, 2)} %
              </Typography>
            </Paper>
          )}
        </Stack>
      )}

      {/* ── Main table ── */}
      {rows.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "72vh", overflow: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              {/* ── Group header row ── */}
              <TableRow>
                <TableCell colSpan={7} sx={{ ...hCell, bgcolor: "#f5f5f5", borderRight: "2px solid #e0e0e0" }} />
                <TableCell
                  colSpan={7}
                  align="center"
                  sx={{ ...hCell, bgcolor: "#fffde7", borderRight: "2px solid #e0e0e0" }}
                >
                  TOTAL COSTS (EUR)
                </TableCell>
                <TableCell
                  colSpan={5}
                  align="center"
                  sx={{ ...hCell, bgcolor: "#e3f2fd", borderRight: "2px solid #e0e0e0" }}
                >
                  COST BY 1 KG (EUR/kg)
                </TableCell>
                <TableCell colSpan={2} align="center" sx={{ ...hCell, bgcolor: "#f3e5f5" }}>
                  OTHER
                </TableCell>
              </TableRow>

              {/* ── Column header row ── */}
              <TableRow>
                {/* Batch info */}
                <TableCell sx={{ ...hCell, bgcolor: "#fafafa" }}>Blend</TableCell>
                <TableCell sx={{ ...hCell, bgcolor: "#fafafa" }}>Batch ID</TableCell>
                <TableCell sx={{ ...hCell, bgcolor: "#fafafa" }}>Batch #</TableCell>
                <TableCell sx={{ ...hCell, bgcolor: "#fafafa" }}>DIET Batch ID</TableCell>
                <TableCell sx={{ ...hCell, bgcolor: "#fafafa" }}>Processing Date</TableCell>
                <TableCell align="right" sx={{ ...hCell, bgcolor: "#fafafa"}}>
                  Raw In
                </TableCell>
                <TableCell align="right" sx={{ ...hCell, bgcolor: "#fafafa", borderRight: "2px solid #e0e0e0" }}>
                  DIET Out Net (kg)
                </TableCell>

                {/* Total costs */}
                <TableCell align="right" sx={{ ...hCell, bgcolor: "#fffde7" }}>Tobacco (net)</TableCell>
                <TableCell align="right" sx={{ ...hCell, bgcolor: "#fff8e1" }}>Custom Tax</TableCell>
                <TableCell align="right" sx={{ ...hCell, bgcolor: "#fffde7" }}>Tobacco + PG</TableCell>
                <TableCell align="right" sx={{ ...hCell, bgcolor: "#e3f2fd" }}>Packaging</TableCell>
                <TableCell align="right" sx={{ ...hCell, bgcolor: "#f3e5f5" }}>Processing</TableCell>
                <TableCell align="right" sx={{ ...hCell, bgcolor: "#fffde7", fontWeight: 800 }}>
                  Total (excl. admin)
                </TableCell>
                <TableCell align="right" sx={{ ...hCell, bgcolor: "#fce4ec", borderRight: "2px solid #e0e0e0" }}>
                  Admin Cost (EUR/kg)
                </TableCell>

                {/* Per kg */}
                <TableCell align="right" sx={{ ...hCell, bgcolor: "#e8f5e9" }}>Tobacco/kg</TableCell>
                <TableCell align="right" sx={{ ...hCell, bgcolor: "#f0f4c3" }}>Custom Tax/kg</TableCell>
                <TableCell align="right" sx={{ ...hCell, bgcolor: "#e3f2fd" }}>Packaging/kg</TableCell>
                <TableCell align="right" sx={{ ...hCell, bgcolor: "#f3e5f5" }}>Processing/kg</TableCell>
                <TableCell align="right" sx={{ ...hCell, bgcolor: "#c8e6c9", fontWeight: 800, borderRight: "2px solid #e0e0e0" }}>
                  Total/kg (incl. admin)
                </TableCell>

                {/* Other */}
                <TableCell align="right" sx={{ ...hCell, bgcolor: "#ede7f6" }}>Wastage %</TableCell>
                <TableCell align="right" sx={{ ...hCell, bgcolor: "#fff3e0" }}>Stored Unit Price</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map(({ batch, costs: c, loading, error }, idx) => {
                const processingDate =
                  batch.processingDate ?? batch.dietProcessingDate ?? batch.primaryProcessingDate;
                const isOdd = idx % 2 === 1;
                const rowBg = isOdd ? "#fafafa" : "#fff";

                if (loading) {
                  return (
                    <TableRow key={batch.id} sx={{ bgcolor: rowBg }}>
                      <TableCell sx={dCell}>{batch.blendName}</TableCell>
                      <TableCell sx={dCell}>{batch.id}</TableCell>
                      <TableCell colSpan={18} sx={{ ...dCell, color: "text.secondary" }}>
                        <CircularProgress size={12} sx={{ mr: 1 }} />
                        Loading…
                      </TableCell>
                    </TableRow>
                  );
                }

                return (
                  <TableRow key={batch.id} hover sx={{ bgcolor: rowBg }}>
                    {/* Batch info */}
                    <TableCell sx={{ ...dCell, fontWeight: 600 }}>{batch.blendName}</TableCell>
                    <TableCell sx={{ ...dCell, fontFamily: "monospace" }}>{batch.id}</TableCell>
                    <TableCell sx={{ ...dCell, fontFamily: "monospace" }}>
                      {batch.primaryId || batch.batchNumber || "—"}
                    </TableCell>
                    <TableCell sx={{ ...dCell, fontFamily: "monospace" }}>
                      {batch.dietId || "—"}
                    </TableCell>
                    <TableCell sx={dCell}>{fmtDate(processingDate)}</TableCell>
                    <TableCell align="right" sx={{ ...dCell}}>
                      {fmt(c?.rawIn, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...dCell, borderRight: "2px solid #e0e0e0" }}>
                      {fmt(c?.dietOutNet, 0)}
                    </TableCell>

                    {/* Total costs */}
                    <TableCell align="right" sx={{ ...dCell, bgcolor: isOdd ? "#fafbe8" : "#fffde7" }}>
                      {fmt(c?.tobaccoNet)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...dCell, bgcolor: isOdd ? "#fff9e6" : "#fff8e1" }}>
                      {fmt(c?.customsTax)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...dCell, fontWeight: 600, bgcolor: isOdd ? "#fafbe8" : "#fffde7" }}>
                      {fmt(c?.tobaccoAndPg)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...dCell, bgcolor: isOdd ? "#e9f4fd" : "#e3f2fd" }}>
                      {fmt(c?.packaging)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...dCell, bgcolor: isOdd ? "#f5e8fc" : "#f3e5f5" }}>
                      {fmt(c?.processing)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...dCell, fontWeight: 700, bgcolor: isOdd ? "#fafbe8" : "#fffde7" }}>
                      {fmt(c?.totalCostEur)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...dCell, bgcolor: isOdd ? "#fde8ef" : "#fce4ec", borderRight: "2px solid #e0e0e0" }}>
                      {fmt(c?.adminTotal)}
                      {c?.adminCostRate != null && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {fmt(c.adminCostRate, 4)}/kg
                        </Typography>
                      )}
                    </TableCell>

                    {/* Per kg */}
                    <TableCell align="right" sx={{ ...dCell, bgcolor: isOdd ? "#ebf7ee" : "#e8f5e9" }}>
                      {fmt(c?.tobaccoPerKg, 4)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...dCell, bgcolor: isOdd ? "#f2f5c8" : "#f0f4c3" }}>
                      {fmt(c?.customsPerKg, 4)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...dCell, bgcolor: isOdd ? "#e9f4fd" : "#e3f2fd" }}>
                      {fmt(c?.packagingPerKg, 4)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...dCell, bgcolor: isOdd ? "#f5e8fc" : "#f3e5f5" }}>
                      {fmt(c?.processingPerKg, 4)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...dCell, fontWeight: 700, bgcolor: isOdd ? "#ceebd0" : "#c8e6c9", borderRight: "2px solid #e0e0e0" }}
                    >
                      {fmt(c?.unitPriceWithAdmin, 4)}
                    </TableCell>

                    {/* Other */}
                    <TableCell
                      align="right"
                      sx={{
                        ...dCell,
                        fontWeight: 600,
                        color:
                          c?.wastage != null
                            ? c.wastage > 15
                              ? "error.main"
                              : "success.dark"
                            : undefined,
                      }}
                    >
                      {c?.wastage != null ? `${fmt(c.wastage, 2)} %` : "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ ...dCell, bgcolor: isOdd ? "#fff6e5" : "#fff3e0" }}>
                      {c?.storedUnitPrice != null ? fmt(c.storedUnitPrice, 4) : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}

              {/* Totals row */}
              {loaded.length > 1 && (
                <TableRow sx={{ bgcolor: "#e8f5e9", position: "sticky", bottom: 0 }}>
                  <TableCell colSpan={5} sx={{ fontWeight: 700, fontSize: "0.8rem" }}>
                    TOTAL ({loaded.length} batches)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.8rem", borderRight: "2px solid #e0e0e0" }}>
                    {fmt(totals.rawIn, 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.8rem", borderRight: "2px solid #e0e0e0" }}>
                    {fmt(totals.dietOutNet, 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{fmt(totals.tobaccoNet)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{fmt(totals.customsTax)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{fmt(totals.tobaccoAndPg)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{fmt(totals.packaging)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{fmt(totals.processing)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{fmt(totals.totalCostEur)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.8rem", borderRight: "2px solid #e0e0e0" }}>
                    {fmt(totals.adminTotal)}
                  </TableCell>
                  {/* Per-kg totals use weighted average */}
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.8rem" }}>
                    {totals.dietOutNet > 0 ? fmt(totals.tobaccoNet / totals.dietOutNet, 4) : "—"}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.8rem" }}>
                    {totals.dietOutNet > 0 ? fmt(totals.customsTax / totals.dietOutNet, 4) : "—"}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.8rem" }}>
                    {totals.dietOutNet > 0 ? fmt(totals.packaging / totals.dietOutNet, 4) : "—"}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.8rem" }}>
                    {totals.dietOutNet > 0 ? fmt(totals.processing / totals.dietOutNet, 4) : "—"}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.8rem", borderRight: "2px solid #e0e0e0" }}>
                    {totalUnitPriceWithAdmin != null ? fmt(totalUnitPriceWithAdmin, 4) : "—"}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.8rem" }}>
                    {totalWastage != null ? `${fmt(totalWastage, 2)} %` : "—"}
                  </TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loadingBatches && rows.length === 0 && selectedBlendIds.length > 0 && (
        <Alert severity="info">No batches found for the selected blend(s).</Alert>
      )}
    </Box>
  );
}
